import { useEffect } from 'react';

type KeyCode =
  | 'Escape'
  | 'Enter'
  | 'ArrowDown'
  | 'ArrowUp'
  | 'Backspace'
  | string;

type KeymapOptions = {
  shift?: boolean;
  ctrl?: boolean;
};

// 전역 키맵 스택을 관리하는 Map
const keymapStack = new Map<
  string,
  Array<{
    action: (e: KeyboardEvent) => void;
    options?: KeymapOptions;
  }>
>();

// 전역 이벤트 핸들러
const handleKeyEvent = (e: KeyboardEvent) => {
  const handlers = keymapStack.get(e.code);
  if (!handlers?.length) return;

  // 스택의 top에 있는 핸들러부터 검사
  for (let i = handlers.length - 1; i >= 0; i--) {
    const { action, options } = handlers[i];

    if (!!options?.ctrl !== !!(e.ctrlKey || e.metaKey)) continue;
    if (!!options?.shift !== !!e.shiftKey) continue;

    action(e);
    e.preventDefault();
    break; // 첫 번째 매칭되는 핸들러만 실행
  }
};

// 전역 이벤트 리스너 등록
window.addEventListener('keydown', handleKeyEvent);

const useKeymap = (
  keyName?: KeyCode,
  action?: (e: KeyboardEvent) => void,
  options?: KeymapOptions,
) => {
  useEffect(() => {
    if (!keyName || !action) return;

    // 스택에 새로운 핸들러 추가
    const handlers = keymapStack.get(keyName) || [];
    keymapStack.set(keyName, [...handlers, { action, options }]);

    // cleanup: 스택에서 핸들러 제거
    return () => {
      const handlers = keymapStack.get(keyName) || [];
      const index = handlers.findIndex((h) => h.action === action);
      if (index !== -1) {
        handlers.splice(index, 1);
        if (handlers.length === 0) {
          keymapStack.delete(keyName);
        } else {
          keymapStack.set(keyName, handlers);
        }
      }
    };
  }, [action]);
};

export default useKeymap;
